import { useDeviceDetect } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { DeviceTypeEnum, SkillEnum } from '@doltech/utils/lib/constants';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS, Step } from 'react-joyride';

import { useTestSubmissionStore } from '../../model/test-submissions.hook';
import { updateUserTutorial } from './api/user-profile';
import { defaultGuidedTourProps } from './defaultGuidedTourProps';
import { getReadingGuidedTour } from './readingGuideTourStep.variables';
import { ReadingMobileGuidedTour } from './ReadingMobileGuidedTour';
import { ReadingTabletGuidedTour } from './ReadingTabletGuidedTour';

export const DesktopReadingGuidedTour = observer(() => {
  const [tourStepIndex, setTourStepIndex] = React.useState(0);

  const testSubmissionStore = useTestSubmissionStore();
  const guidedReadingSteps: Step[] = getReadingGuidedTour({
    screenMode: 'desktop',
    sectionCount: testSubmissionStore.testSections.length,
    minutes: Math.floor(testSubmissionStore.totalDuration / 60000),
    // test that have this condition will not show the next/prev button
    isOnlyOneTestSectionAndOneQuestionGroup:
      testSubmissionStore.testSections.length === 1 &&
      testSubmissionStore.testSections?.[0]?.questionGroups?.length === 1,
  });

  React.useEffect(() => {
    testSubmissionStore.tourGuide?.setTotalStep(guidedReadingSteps.length);
  }, [testSubmissionStore.tourGuide, guidedReadingSteps.length]);

  const joyRideCallback = React.useCallback(
    (data) => {
      const { action, index, status, type } = data;
      testSubmissionStore.tourGuide?.setTourIndex(index);
      if (index > 0 && index < guidedReadingSteps.length - 1) {
        const elm: any = document.getElementsByClassName('react-joyride__spotlight')?.[0];
        if (elm) {
          elm.style.left = '0px';
          elm.style.right = '0px';
          elm.style.width = '100%';
        }
      }
      if (index === guidedReadingSteps.length - 1) {
        testSubmissionStore.goToLastQuestionGroup();
      } else {
        testSubmissionStore.goToFirstQuestionGroup();
      }
      if (type === EVENTS.STEP_AFTER) {
        switch (action) {
          case ACTIONS.NEXT: {
            setTourStepIndex(index + 1);
            break;
          }
          case ACTIONS.PREV: {
            setTourStepIndex(index - 1);
            break;
          }
        }
      } else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        testSubmissionStore.goToFirstQuestionGroup();
        updateUserTutorial({ skill: SkillEnum.READING, deviceType: DeviceTypeEnum.DESKTOP });
        testSubmissionStore.tourGuide?.finishTourGuide();
      }
    },
    [guidedReadingSteps.length, testSubmissionStore]
  );

  return (
    <Joyride
      {...defaultGuidedTourProps}
      run={!testSubmissionStore.tourGuide?.isTourGuideFinished}
      callback={joyRideCallback}
      stepIndex={tourStepIndex}
      steps={guidedReadingSteps}
    />
  );
});

interface ReadingGuidedTourProps {
  setViewMode?: () => void;
}

export const ReadingGuidedTour = ({ setViewMode }: ReadingGuidedTourProps) => {
  const { isTablet, isMobile } = useDeviceDetect();

  if (isMobile) return <ReadingMobileGuidedTour setViewMode={setViewMode} />;
  if (isTablet) return <ReadingTabletGuidedTour />;

  return <DesktopReadingGuidedTour />;
};
